/**
 * One card: who it is, its market value from sold sales, the evaluator for a price, and
 * PriceCharting's guide prices by grade with the trend, folded away at the bottom.
 */

import { type ReactNode, useEffect, useState } from "react";
import { ChevronDown, ChevronLeft } from "lucide-react";
import { api, type CardDetail, type Grade, type ValuedGrade } from "./api";
import { Evaluate } from "./Evaluate";
import { cardNumber, day, LANGUAGE_LABEL, usd } from "./format";
import { MarketValue } from "./MarketValue";
import { CardImage, Chip, cx, Skeleton } from "./ui";

const GRADE_LABEL: Array<[Grade, string]> = [
  ["ungraded", "Raw"],
  ["grade7", "Grade 7"],
  ["grade8", "Grade 8"],
  ["grade9", "Grade 9"],
  ["grade95", "Grade 9.5"],
  ["psa10", "PSA 10"],
  ["bgs10", "BGS 10"],
  ["cgc10", "CGC 10"],
];

interface Props {
  id: number;
  onOpen: (id: number) => void;
  onBack: () => void;
  onError: (e: unknown) => void;
}

export function CardPage({ id, onOpen, onBack, onError }: Props) {
  const [card, setCard] = useState<CardDetail | null>(null);
  const [failed, setFailed] = useState(false);
  const [grade, setGrade] = useState<ValuedGrade>("ungraded");

  useEffect(() => {
    let live = true;
    setCard(null);
    setFailed(false);
    api.card(id).then((r) => live && setCard(r.card)).catch((e) => {
      if (!live) return;
      onError(e);
      setFailed(true);
    });
    return () => {
      live = false;
    };
  }, [id, onError]);

  return (
    <div className="space-y-4 px-4 pb-6 pt-3">
      <button type="button" onClick={onBack} className="-ml-1 flex items-center gap-0.5 text-sm font-medium text-navy-700 hover:text-navy-900">
        <ChevronLeft className="h-4 w-4" />
        Back
      </button>

      {failed && <div className="card p-5 text-sm text-rose-700">This card could not be opened.</div>}
      {!failed && !card && <HeaderSkeleton />}

      {card && (
        <>
          <section className="card overflow-hidden animate-fadein">
            <div className="flex gap-4 p-4">
              <CardImage src={card.imageUrl} alt={card.cardName} className="w-28 flex-none shadow-lift sm:w-36" />
              <div className="min-w-0 flex-1 pt-0.5">
                <h1 className="text-[22px] font-semibold leading-tight tracking-tight text-slate-900">{card.cardName}</h1>
                {card.localName && <div className="mt-0.5 text-[15px] text-slate-500">{card.localName}</div>}
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  <Chip tone="navy">{LANGUAGE_LABEL[card.language]}</Chip>
                  {card.setCode && <Chip>{card.setCode}</Chip>}
                  {card.rarity && <Chip tone="gold">{card.rarity}</Chip>}
                </div>
                <dl className="mt-3 space-y-1 text-[13px]">
                  <Row label="Set" value={card.setName} />
                  <Row label="Number" value={<span className="num">{cardNumber(card.number, card.setTotal) || "-"}</span>} />
                  {card.releaseDate && <Row label="Released" value={day(card.releaseDate)} />}
                </dl>
              </div>
            </div>
            {card.siblings.length > 1 && (
              <div className="border-t border-slate-100 px-4 py-3">
                <div className="label mb-2">Variant</div>
                <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-0.5">
                  {card.siblings.map((s) => {
                    const active = s.id === card.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => !active && onOpen(s.id)}
                        className={cx(
                          "flex-none rounded-xl border px-3 py-1.5 text-left transition",
                          active ? "border-navy-900 bg-navy-900 text-white" : "border-slate-200 bg-white hover:border-slate-300",
                        )}
                      >
                        <div className="whitespace-nowrap text-[13px] font-semibold">{s.variants.length ? s.variants.join(", ") : "Normal"}</div>
                        <div className={cx("num whitespace-nowrap text-[11px]", active ? "text-navy-200" : "text-slate-500")}>{usd(s.ungraded)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          <MarketValue cardId={card.id} grade={grade} onGrade={setGrade} onError={onError} />
          <Evaluate key={`${card.id}-${grade}`} cardId={card.id} grade={grade} onError={onError} />
          <GuidePrices card={card} />
        </>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-16 flex-none text-slate-400">{label}</dt>
      <dd className="min-w-0 text-slate-700">{value}</dd>
    </div>
  );
}

function HeaderSkeleton() {
  return (
    <div className="card flex gap-4 p-4">
      <Skeleton className="aspect-[63/88] w-28 flex-none rounded-lg" />
      <div className="flex-1 space-y-3 pt-1">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );
}

function GuidePrices({ card }: { card: CardDetail }) {
  const [open, setOpen] = useState(false);
  const rows = GRADE_LABEL.filter(([g]) => card.prices[g] != null);
  return (
    <section className="card overflow-hidden">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between px-4 py-3.5 text-left">
        <div>
          <div className="label">PriceCharting guide prices</div>
          <div className="mt-0.5 text-xs text-slate-500">Their own estimate per grade, as of {day(card.prices.day)}</div>
        </div>
        <ChevronDown className={cx("h-5 w-5 text-slate-400 transition", open && "rotate-180")} />
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 pb-4 pt-3 animate-fadein">
          <div className="grid grid-cols-2 gap-2">
            {rows.map(([grade, label]) => (
              <div key={grade} className="rounded-xl bg-slate-50 px-3 py-2">
                <div className="text-[11px] font-medium text-slate-500">{label}</div>
                <div className="num text-[15px] font-semibold text-slate-900">{usd(card.prices[grade])}</div>
              </div>
            ))}
          </div>
          {card.history.length > 1 ? (
            <Trend history={card.history} />
          ) : (
            <p className="mt-3 text-xs text-slate-500">The trend line appears once a few days of prices have been kept.</p>
          )}
        </div>
      )}
    </section>
  );
}

/** Raw and PSA 10 guide prices over the days kept so far. */
function Trend({ history }: { history: CardDetail["history"] }) {
  const width = 320;
  const height = 90;
  const series = (["ungraded", "psa10"] as const).map((key) => history.map((h) => h[key]));
  const values = series.flat().filter((v): v is number => v != null);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const x = (i: number) => (i / Math.max(history.length - 1, 1)) * width;
  const y = (v: number) => height - ((v - min) / (max - min || 1)) * (height - 12) - 6;
  const path = (points: Array<number | null>) =>
    points.map((v, i) => (v == null ? "" : `${i === 0 || points[i - 1] == null ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`)).join(" ");
  return (
    <div className="mt-4">
      <div className="mb-1 flex items-center gap-3 text-[11px] text-slate-500">
        <span>Since {day(history[0]?.day)}</span>
        <span className="flex items-center gap-1"><span className="h-0.5 w-3 rounded bg-navy-700" />Raw</span>
        <span className="flex items-center gap-1"><span className="h-0.5 w-3 rounded bg-gold-500" />PSA 10</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="h-24 w-full overflow-visible">
        <path d={path(series[0] ?? [])} fill="none" stroke="#2b2e5e" strokeWidth="2" strokeLinejoin="round" />
        <path d={path(series[1] ?? [])} fill="none" stroke="#d9b25c" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    </div>
  );
}
